/**
 * #1287 — the "OddsPapi: my books" row: which bookmakers this trader's Game
 * Detail OddsPapi table shows, and which its "My books" consensus uses.
 *
 * Two groups of toggles: the books in hydra's reviewed OddsPapi census
 * (Pinnacle first) and real OddsPapi books outside it, which hydra is not
 * expected to deliver (#1287 — which books a deployment actually receives is
 * unverified here; the census is what G1 knows). The
 * second group stays selectable so a trader's whole list survives; those books
 * are marked "not in census" and never contribute. Nothing selected is the default
 * and what Reset returns to: the table as everyone else sees it.
 *
 * Each toggle carries its state in `aria-pressed` and its OddsPapi identifier
 * in its tooltip, so two books with similar names (Sharp / SharpBet) can be
 * told apart by what the feed calls them.
 */
import { useAuthStore } from '@/stores/authStore';
import { selectOwnedPersonalBooks, usePersonalBooks } from '@/stores/personalBooks';
import { BOOK_ORDER, OUTSIDE_CENSUS_BOOKS, oddsBookLabel } from '@/views/GameDetail/oddsBoard';

const CENSUS_BOOKS: readonly string[] = ['pinnacle', ...BOOK_ORDER];

function BookToggles({
  label,
  books,
  selected,
  disabled,
  onToggle,
}: {
  label: string;
  books: readonly string[];
  selected: readonly string[];
  disabled: boolean;
  onToggle: (slug: string) => void;
}) {
  return (
    <div role="group" aria-label={label}>
      <p className="text-muted-foreground mb-1 text-[11px]">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {books.map((slug) => {
          const on = selected.includes(slug);
          return (
            <button
              key={slug}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              title={`OddsPapi bookmaker "${slug}"`}
              onClick={() => onToggle(slug)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                on
                  ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
                  : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              {oddsBookLabel(slug)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PersonalBooksSetting() {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const selected = usePersonalBooks((s) => selectOwnedPersonalBooks(s, viewerId));
  const toggleBook = usePersonalBooks((s) => s.toggleBook);
  const reset = usePersonalBooks((s) => s.reset);
  // Toggles write against the loaded owner; until this viewer's selection is
  // loaded there is nothing honest to show or change.
  const ready = usePersonalBooks((s) => viewerId != null && s.userId === viewerId);
  const outsideCensus = selected.filter((b) => OUTSIDE_CENSUS_BOOKS.includes(b));

  return (
    <div className="space-y-2">
      <BookToggles label="In hydra's reviewed census" books={CENSUS_BOOKS} selected={selected} disabled={!ready} onToggle={toggleBook} />
      <BookToggles
        label="Not in hydra's census"
        books={OUTSIDE_CENSUS_BOOKS}
        selected={selected}
        disabled={!ready}
        onToggle={toggleBook}
      />
      <div className="flex items-center gap-2">
        <p data-testid="personal-books-status" className="text-muted-foreground min-w-0 flex-1 text-xs">
          {selected.length === 0
            ? 'Off — the OddsPapi table shows every book.'
            : `${selected.length} ${selected.length === 1 ? 'book' : 'books'}: ${selected.map(oddsBookLabel).join(', ')}.` +
              (outsideCensus.length > 0
                ? ` ${outsideCensus.length} not in hydra's census: shown, never counted.`
                : '')}
        </p>
        <button
          type="button"
          onClick={reset}
          disabled={!ready || selected.length === 0}
          className="bg-secondary text-muted-foreground hover:text-foreground shrink-0 rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-50"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
